/**
 * Composable pour gérer les permissions et vérifications d'accès
 */
import { computed } from 'vue'
import { useAuth } from './useAuth.js'
import { hasAdminRights, isSuperAdmin as isSuperAdminRole, isCommercial as isCommercialRole, aLeRoleComplementaire, ROLES_COMPLEMENTAIRES } from '../../constants/roles.js'

export function usePermissions(resource = null) {
  const { user: currentUser } = useAuth()

  /**
   * Vérifie si l'utilisateur a les droits d'administration (admin OU super_admin).
   */
  const isAdmin = computed(() => {
    return hasAdminRights(currentUser.value?.role)
  })

  /**
   * Vérifie si l'utilisateur est spécifiquement super_admin — seul rôle avec
   * accès en plus au panneau d'activité commerciale.
   */
  const isSuperAdmin = computed(() => {
    return isSuperAdminRole(currentUser.value?.role)
  })

  /**
   * Accès à la page Pointeuse : rôle complémentaire `pointeuse` uniquement,
   * aucun rôle principal (même super_admin) ne l'accorde.
   */
  const hasAccesPointeuse = computed(() => {
    return aLeRoleComplementaire(currentUser.value, ROLES_COMPLEMENTAIRES.POINTEUSE)
  })

  /**
   * Vérifie si l'utilisateur est commercial
   */
  const isCommercial = computed(() => {
    return isCommercialRole(currentUser.value?.role)
  })

  /**
   * Vérifie si l'utilisateur peut éditer une ressource
   * @param {Object} resourceData - Données de la ressource (doit avoir assigned_user_id)
   * @returns {boolean} - True si l'utilisateur peut éditer
   */
  const canEdit = (resourceData = null) => {
    const data = resourceData || resource
    if (!currentUser.value || !data) return false
    
    // Admin peut tout éditer
    if (isAdmin.value) return true
    
    // Commercial peut éditer seulement ce qui lui est assigné
    if (isCommercial.value) {
      return data.assigned_user_id === currentUser.value.id
    }
    
    return false
  }

  /**
   * Vérifie si l'utilisateur peut changer le statut d'une ressource
   * @param {Object} resourceData - Données de la ressource
   * @returns {boolean} - True si l'utilisateur peut changer le statut
   */
  const canChangeStatus = (resourceData = null) => {
    return canEdit(resourceData)
  }

  /**
   * Vérifie si l'utilisateur peut assigner un commercial
   * @returns {boolean} - True si l'utilisateur peut assigner
   */
  const canAssign = computed(() => {
    // Admin et commerciaux peuvent assigner
    return isAdmin.value || isCommercial.value
  })

  /**
   * Vérifie si l'utilisateur peut supprimer une ressource
   * @param {Object} resourceData - Données de la ressource
   * @returns {boolean} - True si l'utilisateur peut supprimer
   */
  const canDelete = (resourceData = null) => {
    return canEdit(resourceData)
  }

  /**
   * Filtre une liste de commerciaux selon les permissions
   * @param {Array} commercials - Liste des commerciaux
   * @returns {Array} - Liste filtrée
   */
  const filterCommercials = (commercials) => {
    // Admin et commerciaux peuvent voir tous les commerciaux
    if (isAdmin.value || isCommercial.value) {
      return commercials
    }
    return []
  }

  /**
   * Obtient les commerciaux disponibles pour un select
   * Inclut le commercial assigné même s'il n'est pas dans la liste filtrée
   * @param {Array} commercials - Liste complète des commerciaux
   * @param {Object} resourceData - Données de la ressource avec assigned_user_id
   * @returns {Array} - Liste des commerciaux pour le select
   */
  const getCommercialsForSelect = (commercials, resourceData = null) => {
    const data = resourceData || resource
    const baseCommercials = filterCommercials(commercials)
    
    if (data?.assigned_user_id) {
      const assignedCommercial = commercials.find(c => c.id === data.assigned_user_id)
      if (assignedCommercial && !baseCommercials.find(c => c.id === assignedCommercial.id)) {
        return [...baseCommercials, assignedCommercial]
      }
    }
    
    return baseCommercials
  }

  return {
    currentUser,
    isAdmin,
    isSuperAdmin,
    hasAccesPointeuse,
    isCommercial,
    canEdit,
    canChangeStatus,
    canAssign,
    canDelete,
    filterCommercials,
    getCommercialsForSelect
  }
}

